// Onboarding flow (docs/06 §2, job J7): first-run to callable in under a
// minute, no forms, no email. Internal step state machine — no router
// dependency, so the shell (src/shell/App.tsx) can mount it standalone.
//
// The recovery phrase and seed live only in this component's React state
// (and the sub-steps it hands them to): never written to storage, never
// logged, never sent over the network. onComplete (identity persisted +
// registered + logged in, including the 2FA password when required) fires
// as soon as the identity is settled — BEFORE the permission-priming steps
// — so quitting mid-permissions never loses the authentication. onFinished
// fires once the whole flow (permissions included) is walked.
//
// Navigation is history-backed: every forward step pushes a real WebView
// history entry, so the Android system back button (MainActivity's
// OnBackPressedCallback pops WebView history), the browser back button and
// the in-UI Back buttons all converge on the single popstate handler below.
// Landing back on 'welcome' forgets any half-built identity, so a later
// "Create account" can never accidentally reuse it.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ApiError,
  PasswordRequiredError,
  generateIdentity,
  seedToPhrase,
  type IdentityKeys,
} from '../identity/index.js';
import { DEV_BUILD } from '../shell/config.js';
import { isCapacitorNative } from '../shell/capacitor/platform.js';
import { supportsWebAuthn } from '../identity/index.js';
import { useT } from '../i18n/index.js';
import { useIsDesktop } from '../screens/ui/AppShell.js';
import { ConfirmPhrase } from './steps/ConfirmPhrase.js';
import { DisplayName } from './steps/DisplayName.js';
import { Microphone } from './steps/Microphone.js';
import { PickAvatar } from './steps/PickAvatar.js';
import { Notifications } from './steps/Notifications.js';
import { RestorePasskey } from './steps/RestorePasskey.js';
import { RestorePassword } from './steps/RestorePassword.js';
import { RestorePhrase } from './steps/RestorePhrase.js';
import { ShowPhrase } from './steps/ShowPhrase.js';
import { Welcome } from './steps/Welcome.js';
import type { OnboardingResult } from './types.js';

type Step =
  | 'welcome'
  | 'showPhrase'
  | 'confirmPhrase'
  | 'restorePhrase'
  | 'restorePasskey'
  | 'restorePassword'
  | 'displayName'
  | 'pickAvatar'
  | 'signingIn'
  | 'microphone'
  | 'notifications';

export function OnboardingFlow({
  onComplete,
  onFinished,
  onPasskeyRestore,
  onPrimePasskeyRestore,
}: {
  onComplete: (result: OnboardingResult) => void | Promise<void>;
  /** The whole flow (auth + permission priming) is done: leave onboarding. */
  onFinished: () => void;
  /**
   * Runs the passkey PRF restore (docs/03 §2): discoverable/one-tap when
   * called without a userId, targeted for a typed account UUID (legacy).
   * Absent ⇒ the "Restore with a passkey" entry is hidden (no WebAuthn
   * platform), so this path never appears where it can't work.
   */
  onPasskeyRestore?: (userId?: string) => Promise<IdentityKeys | undefined>;
  /**
   * Best-effort options prefetch fired when the passkey-restore step mounts,
   * so the ceremony starts with no network await inside the tap (Safari's
   * transient-activation window — web/passkey-audit.md §1b).
   */
  onPrimePasskeyRestore?: () => void;
}): ReactNode {
  const t = useT();
  const isDesktop = useIsDesktop();
  const [step, setStep] = useState<Step>('welcome');
  const [identity, setIdentity] = useState<IdentityKeys | undefined>(undefined);
  const [restored, setRestored] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [pwError, setPwError] = useState<string | undefined>(undefined);
  const [pwBusy, setPwBusy] = useState(false);
  const completedRef = useRef(false);
  // Steps behind the current one; its length is exactly the number of
  // history entries this flow has pushed (welcome pushes none).
  const stackRef = useRef<Step[]>([]);
  // Stamped into every pushed entry: a restored/stale history entry from a
  // previous WebView session must read as "before onboarding", not as a
  // trusted depth.
  const sessionIdRef = useRef(`${Date.now()}-${Math.random()}`);

  // Recomputed only when the identity changes (new seed generated, or a
  // restore resolves) — never touches storage or the network.
  const words = useMemo(() => (identity ? seedToPhrase(identity.seed).split(' ') : []), [identity]);

  /** Forward navigation: push the current step and a matching history entry. */
  function goTo(next: Step): void {
    stackRef.current.push(step);
    history.pushState({ onbSid: sessionIdRef.current, onbDepth: stackRef.current.length }, '');
    setStep(next);
  }

  /** All Back buttons go through history so system back stays in sync. */
  function goBack(): void {
    history.back();
  }

  /** Abandon the flow entirely (restorePassword's Back): unwind to welcome. */
  function backToWelcome(): void {
    if (stackRef.current.length > 0) history.go(-stackRef.current.length);
  }

  // The single back handler: popstate carries the target depth (0 = the
  // entry that existed before onboarding = welcome); pop the step stack to
  // match. Forward traversals (browser forward button) are ignored — the
  // flow only ever walks forward via goTo.
  useEffect(() => {
    function onPop(e: PopStateEvent): void {
      if (completedRef.current) return; // completion unwind, not user nav
      const s = e.state as { onbSid?: unknown; onbDepth?: unknown } | null;
      const target =
        s?.onbSid === sessionIdRef.current && typeof s.onbDepth === 'number' ? s.onbDepth : 0;
      const stack = stackRef.current;
      if (target >= stack.length) return;
      const landing = stack[target] ?? 'welcome';
      stackRef.current = stack.slice(0, target);
      if (landing === 'welcome') {
        setIdentity(undefined);
        setRestored(false);
        setPwError(undefined);
      }
      setStep(landing);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function handleCreateAccount(): void {
    setIdentity(generateIdentity());
    setRestored(false);
    goTo('showPhrase');
  }

  // A restored account already has its server-side display name (register is
  // idempotent and keeps it), so the name step is create-path only — restore
  // authenticates immediately. The name is editable in Settings regardless.
  function handleRestored(keys: IdentityKeys): void {
    setIdentity(keys);
    setRestored(true);
    void authenticate({ identity: keys, restored: true });
  }

  // Create path: the display name is settled, then the avatar step (a fresh
  // account has no server-side avatar yet). Restore paths never reach here —
  // handleRestored authenticates straight away, keeping the account's avatar.
  function handleDisplayNameContinue(name: string): void {
    setDisplayName(name);
    goTo('pickAvatar');
  }

  /**
   * The auth step: persist the identity and register/log in (via onComplete)
   * BEFORE any permission priming, so quitting mid-permissions never loses
   * the account. Recoverable failure: a restored account with a password
   * (2FA, docs/03 §3a) throws PasswordRequiredError → the password step
   * shows and retries with the typed password. During an active password
   * submission real errors surface on that step; everything else resolves
   * offline-tolerantly in the shell. On success the flow's history entries
   * unwind (completedRef makes the popstate a no-op) and the permission
   * steps follow OUTSIDE history — there is nothing to go back to.
   * Overrides carry values whose setState hasn't rendered yet.
   */
  async function authenticate(opts: {
    password?: string;
    identity?: IdentityKeys;
    restored?: boolean;
    name?: string;
    avatarSeed?: number;
  }): Promise<void> {
    const id = opts.identity ?? identity;
    if (completedRef.current || !id) return;
    setPwBusy(true);
    const origin = step;
    if (origin !== 'restorePassword') setStep('signingIn');
    try {
      await onComplete({
        identity: id,
        displayName: opts.name ?? displayName,
        restored: opts.restored ?? restored,
        ...(opts.password ? { restorePassword: opts.password } : {}),
        ...(opts.avatarSeed !== undefined ? { avatarSeed: opts.avatarSeed } : {}),
      });
      completedRef.current = true;
      if (stackRef.current.length > 0) history.go(-stackRef.current.length);
      // The mic-priming step only matters on native (the WebView getUserMedia
      // prompt can't show over the keyguard); the browser PWA prompts fine at
      // call time, so it goes straight to notifications.
      setStep(isCapacitorNative() ? 'microphone' : 'notifications');
    } catch (err) {
      if (err instanceof PasswordRequiredError) {
        setPwError(opts.password ? t('restorePassword.wrong') : undefined);
        // Deliberately NOT pushed onto history: system back from the password
        // step pops to the restore screen it came from.
        setStep('restorePassword');
      } else if (opts.password) {
        // 429 = the per-account challenge budget; the password was not even
        // checked. Anything else here is a connection-shaped failure.
        setPwError(
          err instanceof ApiError && err.status === 429
            ? t('restorePassword.tooMany')
            : t('restorePassword.checkFailed'),
        );
        setStep('restorePassword');
      } else {
        // Unexpected pre-password failure: return where the user was.
        setStep(origin);
      }
    } finally {
      setPwBusy(false);
    }
  }

  function renderStep(): ReactNode {
    switch (step) {
    case 'welcome':
      return (
        <Welcome
          onCreateAccount={handleCreateAccount}
          onRestoreAccount={() => goTo('restorePhrase')}
          {...(onPasskeyRestore && supportsWebAuthn()
            ? { onRestorePasskey: () => goTo('restorePasskey') }
            : {})}
        />
      );
    case 'showPhrase':
      return (
        <ShowPhrase
          words={words}
          onContinue={() => goTo('confirmPhrase')}
          onBack={goBack}
          {...(DEV_BUILD ? { onDevSkip: () => goTo('displayName') } : {})}
        />
      );
    case 'confirmPhrase':
      return (
        <ConfirmPhrase words={words} onConfirmed={() => goTo('displayName')} onBack={goBack} />
      );
    case 'restorePhrase':
      return <RestorePhrase onRestored={handleRestored} onBack={goBack} />;
    case 'restorePasskey':
      return onPasskeyRestore ? (
        <RestorePasskey
          onRestore={onPasskeyRestore}
          onRestored={handleRestored}
          onBack={goBack}
          onUsePhrase={() => goTo('restorePhrase')}
          {...(onPrimePasskeyRestore ? { onPrime: onPrimePasskeyRestore } : {})}
        />
      ) : (
        <RestorePhrase onRestored={handleRestored} onBack={goBack} />
      );
    case 'restorePassword':
      return (
        <RestorePassword
          onSubmit={(pw) => void authenticate({ password: pw })}
          onBack={backToWelcome}
          busy={pwBusy}
          {...(pwError !== undefined ? { error: pwError } : {})}
        />
      );
    case 'displayName':
      return <DisplayName onContinue={handleDisplayNameContinue} onBack={goBack} />;
    case 'pickAvatar':
      // The userId is minted server-side at register, so the picker is seeded
      // by the identity public key (stable per identity); only the chosen index
      // persists. `identity` is always set on the create path that reaches here.
      return identity ? (
        <PickAvatar
          userId={identity.publicKeyB64}
          onContinue={(seed) => void authenticate({ avatarSeed: seed })}
          onSkip={() => void authenticate({})}
          onBack={goBack}
        />
      ) : null;
    case 'signingIn':
      return (
        <main className="mx-auto flex min-h-full w-full max-w-app flex-col items-center justify-center gap-4 p-6">
          <p role="status" className="text-lg text-muted">
            {t('restorePassword.signingIn')}
          </p>
        </main>
      );
    // Post-auth permission priming: the account is already persisted and
    // logged in — no Back buttons, no history entries; quitting here loses
    // nothing (the prompts re-arm at call time).
    case 'microphone':
      return <Microphone onNext={() => setStep('notifications')} />;
    case 'notifications':
      return <Notifications onNext={onFinished} />;
    default:
      return null;
    }
  }

  const content = renderStep();

  // Compact stays byte-for-byte the bare step (the phone column). Desktop wraps
  // the same step content in a centered surface card on the window background —
  // presentation only, no change to step logic or order.
  if (!isDesktop) return content;
  return (
    <div className="flex min-h-full items-center justify-center bg-bg p-6">
      <div className="w-full max-w-[440px] overflow-hidden rounded-2xl border border-edge bg-surface">
        {content}
      </div>
    </div>
  );
}
